import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { debounceTime, Subject } from 'rxjs';
import { DocumentFilters } from 'src/app/models/document';
import { MessageService } from '../../services/message.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Option } from '../select/select.component';
import { Case, CaseType } from 'src/app/models/case';
import { Trademark } from 'src/app/models/trademark';
import { AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';

export type Level = {
  [K in 1 | 2 | 3]: string
}

@Component({
  selector: 'app-documents-filter-drawer',
  templateUrl: './documents-filter-drawer.component.html',
  styleUrls: ['./documents-filter-drawer.component.scss']
})
export class DocumentsFilterDrawerComponent implements AfterViewInit, OnChanges {
  CaseType = CaseType;

  @ViewChild('drawer') drawer!: MatDrawer;

  @Input() entityKey: string = 'ENTITIES.DOCUMENT'
  @Input() displayMode: string = 'table';
  @Input() count: number = 0;
  @Input() levels: Level = {
    1: 'trademark',
    2: 'territory',
    3: 'nice'
  };
  // Inside a case, the filters only offer what the case covers: its trademark (so no
  // trademark or family filter), its Nice classes, and the countries of its office
  @Input() case?: Case;
  @Input() relevantTerritories: string[] = [];
  caseTrademarks: Partial<Trademark>[] = [];
  caseClasses: number[] = [];

  @Input() filters!: DocumentFilters;
  @Output() filtersChange = new EventEmitter<DocumentFilters>();

  private readonly filtersSubject = new Subject<DocumentFilters>();

  public uploadingBox: boolean = false;

  confidentialOptions: Option[] = [
    {
      value: 'confidential',
      label: 'CONFIDENTIALITY.CONFIDENTIAL',
      selected: false
    },
    {
      value: 'notConfidential',
      label: 'CONFIDENTIALITY.NOT_CONFIDENTIAL',
      selected: false
    }
  ];

  levelOptions: Option[] = [
    {
      value: 'trademark',
      label: 'ENTITIES.TRADEMARK',
      selected: false
    },
    {
      value: 'territory',
      label: 'DOCUMENTS_HIERARCHY.TERRITORY',
      selected: false
    },
    {
      value: 'nice',
      label: 'DOCUMENTS_HIERARCHY.NICE_CLASSIFICATION',
      selected: false
    },
    {
      value: 'products_services',
      label: 'DOCUMENTS_HIERARCHY.PRODUCTS_SERVICES',
      selected: false
    },
    {
      value: 'year',
      label: 'DOCUMENTS_HIERARCHY.YEAR',
      selected: false
    },
    {
      value: 'language',
      label: 'DOCUMENTS_HIERARCHY.LANGUAGE',
      selected: false
    },
    {
      value: 'class',
      label: 'DOCUMENTS_HIERARCHY.DOCUMENT_CLASS',
      selected: false
    }
  ];

  level1Options: Option[] = [];
  level2Options: Option[] = [];
  level3Options: Option[] = [];

  get filtersCount(): number {
    return (this.filters.approval.length > 0 ? 1 : 0)
      + (this.filters.classNumber.length > 0 ? 1 : 0)
      + (this.filters.territories.length > 0 ? 1 : 0)
      + (this.filters.documentClass.length > 0 ? 1 : 0)
      + (this.filters.trademark.length > 0 ? 1 : 0)
      + (this.filters.trademarkFamily.length > 0 ? 1 : 0)
      + ((this.filters.period.start !== undefined || this.filters.period.end !== undefined) ? 1 : 0)
      + (this.filters.productsServices.length > 0 ? 1 : 0)
      + (this.filters.tags.length > 0 ? 1 : 0)
      + (this.filters.isConfidential !== undefined ? 1 : 0)
  }

  constructor(
    private readonly _message: MessageService
  ) {
    this.level1Options = this.levelOptions.map((option) => ({ ...option, selected: option.value === this.levels[1] }));
    this.level2Options = this.levelOptions.map((option) => ({ ...option, selected: option.value === this.levels[2] }));
    this.level3Options = this.levelOptions.map((option) => ({ ...option, selected: option.value === this.levels[3] }));

    this._message.changeEmitted$
      .pipe(takeUntilDestroyed())
      .subscribe(change => {
        if (change && change.key == 'UPLOAD.SHOW') {
          this.uploadingBox = true;
        }
        if (change && change.key == 'UPLOAD.HIDE') {
          this.uploadingBox = false;
        }
      });

    this.filtersSubject
      .pipe(
        debounceTime(300),
        takeUntilDestroyed()
      ).subscribe((filters) => {
        this.filtersChange.emit(filters)
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['case']) {
      this.caseTrademarks = this.case?.trademark?.ref ? [{ _id: this.case.trademark.ref }] : [];
      this.caseClasses = this.case?.niceClassification ?? [];
    }
  }

  ngAfterViewInit(): void {
    if (this.case?.type === CaseType.RENEWAL_MAINTENANCE) {
      this.levelOptions = this.levelOptions.filter((option) => option.value !== 'trademark' && option.value !== 'territory');
    }
  }


  public toggleDrawer(): void {
    void this.drawer.toggle();
  }

  public closeDrawer(): void {
    void this.drawer.close();
  }

  private emitFiltersChange(filters: DocumentFilters): void {
    this.filtersSubject.next(filters);
  }

  public onLevelChange(level: 1 | 2 | 3, event: any): void {
    if (event && event.value && event.value.value) {
      this.levels[level] = event.value.value;
    }
  }

  public onQueryChange(query: any): void {
    if (typeof query === 'string') {
      this.emitFiltersChange(this.filters);
    }
  }

  public onDocumentApprovalChange(event: any): void {
    if (event?.value) {
      this.filters.approval = event.value.map((appr: any) => appr.value);
      this.emitFiltersChange(this.filters);
    }
  }

  public onDocumentClassChange(event: any): void {
    if (event?.value) {
      this.filters.documentClass = event.value.map((v: any) => v.value);
      this.emitFiltersChange(this.filters);
    }
  }

  public onTrademarkFamilyChange(event: any): void {
    if (event?.value) {
      this.filters.trademarkFamily = event.value;
      this.emitFiltersChange(this.filters);
    }
  }

  public onTrademarkChange(event: any): void {
    if (event?.value) {
      this.filters.trademark = event.value;
      this.emitFiltersChange(this.filters);
    }
  }

  public onNiceClassificationChange(event: any): void {
    if (event?.value) {
      this.filters.classNumber = event.value.map((niceClass: any) => niceClass.classNumber);
      this.emitFiltersChange(this.filters);
    }
  }

  public onProductServiceChange(event: any): void {
    if (event?.value) {
      this.filters.productsServices = event.value;
      this.emitFiltersChange(this.filters);
    }
  }

  public onTerritoryChange(event: any): void {
    if (event?.value) {
      this.filters.territories = event.value.map((territory: any) =>
        territory.alternateCodes ? [territory.id, ...territory.alternateCodes] : [territory.id]
      ).flat();
      this.emitFiltersChange(this.filters);
    }
  }

  public onPeriodChange(event: any): void {
    if (event?.hasOwnProperty('start') && event?.hasOwnProperty('end')) {
      this.filters.period = event;
      this.emitFiltersChange(this.filters);
    }
  }

  public onTagChange(event: any): void {
    if (event?.value) {
      this.filters.tags = event.value;
      this.emitFiltersChange(this.filters);
    }
  }

  public onConfidentialChange(event: any): void {
    const raw = event?.value?.value;
    if (raw === 'confidential') {
      this.filters.isConfidential = true;
    } else if (raw === 'notConfidential') {
      this.filters.isConfidential = false;
    } else {
      this.filters.isConfidential = undefined;
    }
    this.emitFiltersChange(this.filters);
  }
}
